import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Send, Loader2, MessageCircle, AlertTriangle, RotateCw, CheckCircle2, Link2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Render a {{n}}-body with the operator's overrides (autofill is server-side).
const renderPreview = (body, template, values) =>
  (body || '').replace(/\{\{(\d+)\}\}/g, (m, n) => {
    const token = (template?.variables || [])[Number(n) - 1];
    const v = token ? values[token] : null;
    return v && String(v).trim() !== '' ? String(v) : `‹${token || n}›`;
  });

const VarInput = ({ token, value, onChange }) => (
  <div>
    <Label className="text-xs text-slate-500 mb-1 block font-mono">{`{${token}}`}</Label>
    <Input
      value={value || ''}
      onChange={(e) => onChange(token, e.target.value)}
      placeholder={`auto-filled if blank`}
      className="h-9 text-sm"
      data-testid={`wa-send-var-${token}`}
    />
  </div>
);

/**
 * WhatsAppSendDialog — Cloud-API send that ALWAYS originates from a CRM record.
 * Props: open, onOpenChange, contextType (trip|lead|invoice|traveller|quote),
 * contextId, title (contact label).
 */
const WhatsAppSendDialog = ({ open, onOpenChange, contextType, contextId, title }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('template');
  const [selectedId, setSelectedId] = useState('');
  const [values, setValues] = useState({});
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [linkInfo, setLinkInfo] = useState(null);
  const [genLoading, setGenLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true); setResult(null); setSelectedId(''); setValues({}); setText(''); setMode('template');
    axios.get(`${BACKEND_URL}/api/whatsapp/templates`, { params: { sendable: true } })
      .then((r) => { if (alive) setTemplates(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) toast.error('Failed to load approved templates'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open]);

  const selected = useMemo(() => templates.find((t) => t.id === selectedId) || null, [templates, selectedId]);

  const linkButton = selected?.buttons?.[0]?.urlVariable ? selected.buttons[0] : null;

  // When a template with a CTA link button is picked, ask the server whether a
  // destination already exists for this record (and if not, if we can make one).
  const refreshLinkInfo = async () => {
    if (!linkButton?.urlVariable) { setLinkInfo(null); return; }
    try {
      const r = await axios.get(`${BACKEND_URL}/api/whatsapp/button-destination`, {
        params: { contextType, contextId, urlVariable: linkButton.urlVariable },
      });
      setLinkInfo(r.data);
    } catch { setLinkInfo(null); }
  };

  useEffect(() => {
    setLinkInfo(null);
    if (!open || !linkButton?.urlVariable) return;
    let alive = true;
    axios.get(`${BACKEND_URL}/api/whatsapp/button-destination`, {
      params: { contextType, contextId, urlVariable: linkButton.urlVariable },
    }).then((r) => { if (alive) setLinkInfo(r.data); }).catch(() => { if (alive) setLinkInfo(null); });
    return () => { alive = false; };
  }, [selectedId, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const doGenerateLink = async () => {
    if (!linkInfo?.linkType) return;
    setGenLoading(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/whatsapp/generate-link`, {
        contextType, contextId, linkType: linkInfo.linkType,
      });
      if (r.data?.ok) {
        toast.success(`${linkInfo.linkType === 'payment' ? 'Payment' : 'Voucher'} link generated`);
        setResult(null);
        await refreshLinkInfo();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate the link');
    } finally {
      setGenLoading(false);
    }
  };

  const setVar = (token, val) => setValues((p) => ({ ...p, [token]: val }));

  const doSend = async () => {
    setSending(true); setResult(null);
    const payload = { contextType, contextId, mode };
    if (mode === 'template') {
      if (!selectedId) { toast.error('Select an approved template'); setSending(false); return; }
      payload.templateId = selectedId;
      payload.variables = values;
    } else {
      if (!text.trim()) { toast.error('Type a reply'); setSending(false); return; }
      payload.text = text.trim();
    }
    try {
      const r = await axios.post(`${BACKEND_URL}/api/whatsapp/send`, payload);
      if (r.data?.ok) {
        setResult({ ok: true });
        toast.success('WhatsApp message sent');
      } else {
        setResult({ ok: false, reason: r.data?.reason, messageId: r.data?.messageId });
        toast.error(r.data?.reason || 'Send failed');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Send failed';
      setResult({ ok: false, reason: detail, blocked: true });
      toast.error(typeof detail === 'string' ? detail : 'Send blocked');
    } finally {
      setSending(false);
    }
  };

  const doRetry = async () => {
    if (!result?.messageId) return;
    setSending(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/whatsapp/messages/${result.messageId}/retry`);
      if (r.data?.ok) { setResult({ ok: true }); toast.success('Retried — message sent'); }
      else { setResult({ ...result, reason: r.data?.reason }); toast.error(r.data?.reason || 'Retry failed'); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retry failed');
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="whatsapp-send-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4 text-emerald-600" /> WhatsApp — Official
            <span className="text-[10px] font-semibold uppercase tracking-wide bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5">Tracked in CRM</span>
          </DialogTitle>
          <DialogDescription>{title ? `To ${title} · logged to this ${contextType}` : `From this ${contextType} record · logged to the CRM`}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading templates…
          </div>
        ) : (
          <div className="space-y-4 py-1">
            {/* Mode toggle */}
            <div className="flex gap-2" data-testid="wa-send-mode">
              <button type="button" onClick={() => setMode('template')} data-testid="wa-mode-template"
                className={`flex-1 py-1.5 rounded-lg text-sm border transition-colors ${mode === 'template' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200'}`}>
                Approved template
              </button>
              <button type="button" onClick={() => setMode('text')} data-testid="wa-mode-text"
                className={`flex-1 py-1.5 rounded-lg text-sm border transition-colors ${mode === 'text' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200'}`}>
                Session reply
              </button>
            </div>

            {mode === 'template' ? (
              <>
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Template</Label>
                  {templates.length === 0 ? (
                    <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      No Meta-approved templates yet. An admin must submit &amp; get templates approved first.
                    </p>
                  ) : (
                    <Select value={selectedId} onValueChange={setSelectedId}>
                      <SelectTrigger className="h-10" data-testid="wa-send-template-select"><SelectValue placeholder="Choose an approved template…" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {templates.map((t) => (
                          <SelectItem key={t.id} value={t.id} data-testid={`wa-send-tpl-${t.id}`}>{t.displayName} · {t.category}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {selected && (selected.variables || []).length > 0 && (
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Variables <span className="text-xs font-normal text-slate-400">(blank = auto-filled from the record)</span></Label>
                    <div className="grid grid-cols-2 gap-3" data-testid="wa-send-variables">
                      {(selected.variables || []).map((token) => (
                        <VarInput key={token} token={token} value={values[token]} onChange={setVar} />
                      ))}
                    </div>
                  </div>
                )}

                {selected && (
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Preview</Label>
                    <div className="rounded-xl bg-[#e7ffdb] border border-emerald-200 px-4 py-3 text-sm text-slate-800 whitespace-pre-line leading-relaxed" data-testid="wa-send-preview">
                      {renderPreview(selected.bodyText, selected, values)}
                    </div>
                  </div>
                )}

                {/* CTA link button — generate a destination on the spot */}
                {selected && linkButton && linkInfo && !linkInfo.hasDestination && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5" data-testid="wa-send-link-box">
                    {linkInfo.canGenerate ? (
                      <div className="flex items-start gap-2">
                        <Link2 className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm text-amber-800">
                            The <strong>“{linkButton.text || 'link'}”</strong> button needs a {linkInfo.linkType} link — none exists for this record yet.
                          </p>
                          <Button size="sm" variant="outline" onClick={doGenerateLink} disabled={genLoading}
                            className="mt-2 border-amber-300 text-amber-800 hover:bg-amber-100"
                            data-testid="wa-generate-link-btn">
                            {genLoading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Link2 className="h-3.5 w-3.5 mr-1" />}
                            Generate {linkInfo.linkType} link
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2" data-testid="wa-send-link-blocked">
                        <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                        <p className="text-sm text-amber-800">
                          {linkInfo.linkType === 'payment' && linkInfo.isFx
                            ? 'This is an FX-mode trip — payment links aren’t auto-generated here. Share the collection details manually or use a template without a payment button.'
                            : 'No destination link is available for this record. Use a template without a link button.'}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                {selected && linkButton && linkInfo?.hasDestination && (
                  <p className="text-xs text-emerald-600 flex items-center gap-1" data-testid="wa-send-link-ready">
                    <CheckCircle2 className="h-3.5 w-3.5" /> {linkInfo.linkType || 'Link'} button is ready.
                  </p>
                )}
              </>
            ) : (
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Reply (only valid inside the 24-hour window)</Label>
                <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={5}
                  placeholder="Type a free-form reply…" className="text-sm" data-testid="wa-send-text" />
                <p className="text-xs text-slate-400 mt-1.5">Outside the 24h window WhatsApp only allows approved templates — you'll be told if that's the case.</p>
              </div>
            )}

            {result && !result.ok && (
              <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2" data-testid="wa-send-failed">
                <AlertTriangle className="h-4 w-4 text-rose-600 mt-0.5 shrink-0" />
                <div className="text-sm text-rose-800 flex-1">
                  <p className="font-semibold">{result.blocked ? 'Blocked' : 'Failed'}</p>
                  <p>{typeof result.reason === 'string' ? result.reason : 'Send failed'}</p>
                  {result.messageId && (
                    <Button size="sm" variant="outline" onClick={doRetry} disabled={sending} className="mt-2" data-testid="wa-send-retry">
                      <RotateCw className="h-3.5 w-3.5 mr-1" /> Retry
                    </Button>
                  )}
                </div>
              </div>
            )}
            {result && result.ok && (
              <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="wa-send-ok">
                <CheckCircle2 className="h-4 w-4" /> Message accepted by WhatsApp.
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="wa-send-cancel">Close</Button>
          <Button onClick={doSend} disabled={sending || (result && result.ok)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="wa-send-submit">
            {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />} Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WhatsAppSendDialog;
